import { useState } from "react";
import { Instagram, Loader2, Download, Copy, BadgeDollarSign, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { reviewShareAssets, reviewShareClaim } from "@/lib/api";

// Meta does not allow apps to post onto a customer's personal Instagram/Facebook,
// so we hand them the branded card + caption: native share sheet on phones
// (opens Instagram/Facebook), download + copy caption on desktop.
export default function ShareReviewButton({ commentRecordId, size = "sm", label = "Share on your Instagram", testId, callerUserRecordId = "", rewardDollars = 0 }) {
  const [busy, setBusy] = useState(false);
  const [assets, setAssets] = useState(null);
  const [opened, setOpened] = useState(false); // desktop fallback shown → allow "I shared it" claim
  const [claimed, setClaimed] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [proof, setProof] = useState("");

  async function claim(network) {
    if (!callerUserRecordId || claimed) return;
    setClaiming(true);
    try {
      const r = await reviewShareClaim(commentRecordId, callerUserRecordId, network, proof.trim());
      setClaimed(true);
      if (r.points_awarded > 0) toast.success(`$${r.points_awarded} reward added for sharing your review (balance ${r.points_display || ""})`, { duration: 8000 });
      else toast.info(r.points_note || "Reward already added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add the reward");
    } finally {
      setClaiming(false);
    }
  }

  async function loadAssets() {
    try {
      return await reviewShareAssets(commentRecordId);
    } catch (e) {
      if (e?.response?.data?.detail) throw e;
      await new Promise((r) => setTimeout(r, 1500)); // transient network/timeout → one retry
      return reviewShareAssets(commentRecordId);
    }
  }

  async function share() {
    setBusy(true);
    try {
      const a = assets || (await loadAssets());
      setAssets(a);
      let file = null;
      try {
        const blob = await (await fetch(a.image_url)).blob();
        file = new File([blob], "my-orevital-review.jpg", { type: "image/jpeg" });
      } catch { /* cross-origin fetch blocked → fall back to download */ }
      if (file && navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: a.caption });
        toast.success("Shared — pick Instagram or Facebook in the sheet");
        await claim("share_sheet");
        return;
      }
      try { await navigator.clipboard.writeText(a.caption); } catch { /* ignore */ }
      window.open(a.image_url, "_blank", "noopener");
      setOpened(true);
      toast.success("Review card opened in a new tab and caption copied — save the image, then post it on Instagram/Facebook and paste the caption.", { duration: 12000 });
    } catch (e) {
      if (e?.name === "AbortError") return;
      toast.error(e?.response?.data?.detail || "Could not prepare the share card — please check your connection and tap Share again");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="inline-flex flex-wrap items-center gap-1.5" data-testid={testId ? `${testId}-wrap` : undefined}>
      <Button type="button" size={size} variant="outline" onClick={share} disabled={busy}
        className="rounded-full border-pink-200 text-pink-700 hover:bg-pink-50 h-8 text-xs" data-testid={testId}>
        {busy ? <Loader2 className="animate-spin" size={13} /> : <><Instagram size={13} className="mr-1" /> {label}</>}
      </Button>
      {assets && (
        <>
          <a href={assets.image_url} download="my-orevital-review.jpg" target="_blank" rel="noreferrer" className="text-pink-700 hover:bg-pink-50 rounded-full p-1.5" title="Download card (post)" data-testid={testId ? `${testId}-download` : undefined}>
            <Download size={13} />
          </a>
          {assets.story_image_url && (
            <a href={assets.story_image_url} download="my-orevital-review-story.jpg" target="_blank" rel="noreferrer" className="text-pink-700 hover:bg-pink-50 rounded-full px-2 py-1 text-[11px] font-semibold inline-flex items-center gap-1" title="Download Story version (9:16)" data-testid={testId ? `${testId}-download-story` : undefined}>
              <Smartphone size={13} /> Story
            </a>
          )}
          <button type="button" onClick={() => { navigator.clipboard.writeText(assets.caption).then(() => toast.success("Caption copied")); }}
            className="text-pink-700 hover:bg-pink-50 rounded-full p-1.5" title="Copy caption" data-testid={testId ? `${testId}-copy` : undefined}>
            <Copy size={13} />
          </button>
        </>
      )}
      {callerUserRecordId && opened && !claimed && (
        <input type="url" value={proof} onChange={(e) => setProof(e.target.value)} placeholder="Paste your post link (optional)"
          className="h-8 w-44 rounded-full border border-amber-200 px-3 text-[11px]" data-testid={testId ? `${testId}-proof` : undefined} />
      )}
      {callerUserRecordId && opened && !claimed && (
        <Button type="button" size="sm" onClick={() => claim("manual")} disabled={claiming}
          className="rounded-full h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white" data-testid={testId ? `${testId}-claim` : undefined}>
          {claiming ? <Loader2 className="animate-spin" size={13} /> : <><BadgeDollarSign size={13} className="mr-1" /> I posted it{rewardDollars ? ` — claim $${rewardDollars}` : ""}</>}
        </Button>
      )}
      {claimed && <span className="text-[11px] font-semibold text-emerald-700" data-testid={testId ? `${testId}-claimed` : undefined}>Reward added ✓</span>}
    </div>
  );
}
